import { AxiosError } from "axios";
import { IconsVariant } from "../../Components/Icon/Icon";
import {
  AlarmFileSounds,
  CalendarUnitEnum,
  DeliveryMethods,
  InternalMessageType,
  PaymentMethodEnum,
  ReceiptEnum,
} from "./enum";

export interface CheckoutSettings {
  item_id: number;
  tipping_is_active: boolean;
  send_tipping_mail: boolean;
  tipping_method: string;
  is_tip_shown_in_live_tracker: boolean;
  how_did_you_find_us_select_is_active: boolean;
}

export interface LinkItem {
  text: string;
  link: string;
  disable?: boolean;
  role?: string;
  layer?: SidebarLayer;
}
export type Status = "online" | "offline";
export type Sizing = "big" | "default" | "small" | "mini";
export type Variants = "primary" | "secondary" | "tertiary";
export interface ProductProp {
  item_id: number;
  item_type: string;
  is_active: boolean;
  code: string;
  name: Record<"all" | "turkish" | "english" | "dutch", string>;
  description: string;
  price: string;
  vat_enum: number;
  image_cloudinary_id: string;
  allergen: number[];
  label: string[];
  recommended_menu_item_id?: number;
  status_enum?: number;
  topping_content_type?: number;
  extended_description?: string;
  extended_media_type_enum?: number[];
  extended_media_value?: string[];
  availability_per_delivery_method?: string;
  availability_per_source?: number;
  price_per_delivery_method?: number;
  price_per_source?: number;
  price_per_target_value?: number;
  time_period?: number;
  category: string;
  topping_categories: any;
  sub_category: any;
  toppings: string[];
  child?: Garnish[];
  target?: number[];
  parent_id: any;
}

export interface Cats {
  content: ProductProp[];
  navigation: Record<number, number[]>;
}

export interface Tag {
  name: string;
  id: number;
}

export type OrderUnitTypes =
  | "daily_openingstime"
  | "daily_calendar_day"
  | "weekly"
  | "monthly"
  | "yearly";

export type SupportedLanguages = "all" | "en" | "sr" | "nl";

export interface MenuItem {
  code: string;
  description: any;
  is_active: boolean;
  item_id: number;
  item_type: string;
  name: any;
  target: number[];
  item_image_and_text_properties: number;
  menu_data_tag_id?: number | null;
  menu_data_size_tag_id?: number | null;
  menu_data_variant_tag_ids?: number[];
}
export interface Menu {
  navigation_content: Category[];
  original_category_ids?: { [productId: string]: number };
}

export interface Category extends MenuItem {
  virtual_item_type?: number;
  target: number[];
  image_cloudinary_id: string;
  sub_category?: any;
  sub_content: Product[];
  back_ground_color_per_target: { [key: string]: string };
  text_color_per_target: { [key: string]: string };
}
export interface Product extends MenuItem {
  original_category_id?: number;
  preparation_time: number;
  price: string;
  vat_enum: number;
  status_enum: number;
  label: string[];
  allergen: number[];
  name_on_receipt: string;
  image_cloudinary_id: string;
  sub_content: Garnish[];
  parent_id?: number;
  color: string;
  text_color: string;
  price_per_target_value: { [key: string]: string };
  vat_enum_per_target: { [key: string]: number };
  vat_percentage_per_target: { [key: string]: number };
  back_ground_color_per_target: { [key: string]: string };
  text_color_per_target: { [key: string]: string };
  barcode?: string[];
  is_free_product?: boolean;
}
export interface Garnish extends MenuItem {
  topping_content_type: number;
  sub_content: Topping[];
}
export interface Topping extends MenuItem {
  vat_enum: number;
  price: string;
  vat_percentage?: number;
  price_per_target_value: { [key: string]: string };
  vat_enum_per_target: { [key: string]: number };
  image_cloudinary_id?: string;
  status_enum?: number;
}

export interface CartItem extends Partial<Omit<Product, "sub_content">> {
  quantity?: number;
  parent_id?: number;
  item_id?: number;
  note?: string;
  sub_content?: CartItem[];
  selected_sub_content?: CartItem[];
  vat_percentage?: number;
  /** AI voice-order suggestion awaiting acceptance; never sent to the cart validator or order endpoints. */
  suggestion?: boolean;
}

export type InnerValue<T> = InnerValueObject<T>[] | T;

export interface InnerValueObject<T = any> {
  [key: string]: InnerObject<T>;
}

export interface InnerObject<T = any> {
  created: string;
  updated: string;
  value: T;
}

export interface CartContent {
  code: string;
  item_id: number;
  name: string;
  parent_name: string;
  single_price_inc_vat: string;
  type: string;
  vat_percentage: string;
  note: string;
  quantity: string;
  toppings?: Topping[];
  payment_method_id?: number;
}

export interface UserInterface {
  branch_id: InnerValue<any>;
  city: InnerValue<any>;
  company_name: InnerValue<any>;
  company_vat_number: InnerValue<any>;
  email: InnerValue<any>;
  first_name: InnerValue<any>;
  hash_crc32: InnerValue<any>;
  house_number: InnerValue<any>;
  house_number_extension: InnerValue<any>;
  last_name: InnerValue<any>;
  name: InnerValue<any>;
  status: InnerValue<any>;
  street_name: InnerValue<any>;
  telephone: InnerValue<any>;
  user_account_id: InnerValue<any>;
  zip_code: InnerValue<any>;
  signed_in_news_letter?: InnerValue<any>;
  signed_in_sms?: InnerValue<any>;
  user_note?: string;
  website_account_id?: InnerValue<any>;
}

export interface Order {
  id?: string;
  cart_content: CartContent[];
  order_information: {
    assigned_courier_id: InnerValue<any>;
    distance_to_branch_in_km: InnerValue<any>;
    cart_item_usage_ids: InnerValue<any>;
    desired_payment_amount: InnerValue<any>;
    device_id: InnerValue<any>;
    hand_over_date: InnerValue<any>;
    hand_over_is_asap: InnerValue<any>;
    hand_over_time: InnerValue<any>;
    hand_over_end_time?: InnerValue<any>;
    hand_over_start_time?: InnerValue<any>;
    ip_address: InnerValue<any>;
    item_id: InnerValue<any>;
    order_status: InnerValue<any>;
    order_payment_status: InnerValue<any>;
    order_user_id: InnerValue<any>;
    payment_method_id: InnerValue<any>;
    placed_date_time: InnerValue<any>;
    public_order_number: InnerValue<any>;
    order_id: number;
    push_token: InnerValue<any>;
    receipt_printer_printed_datetime?: InnerValue<any>;
    remarks: InnerValue<any>;
    source: InnerValue<any>;
    subtotal_prices: InnerValue<any>;
    total_paid: InnerValue<any>;
    total_price: InnerValue<any>;
    tip?: InnerValue<any>;
    delivery_method_id?: InnerValue<any>;
    hand_over_opening_time_date?: InnerValue<any>;
    placed_date_opening_time_date?: InnerValue<any>;
    zipcode_color_code?: InnerValue<any>;
    is_custom_time_slot_selected?: InnerValue<any>;
    custom_time_slot_label?: InnerValue<any>;
  };
  order_user: UserInterface;
}

export interface CmsUsersResponse {
  hourly_wage?: number;
  has_pincode: boolean;
  voip_extension_number: string;
  can_drive: number[];
  accessible_account_id: number;
  activation_code: string;
  address_city: string;
  address_description: string;
  address_house_number: string;
  address_house_number_extension: string;
  address_street: string;
  address_zipcode: string;
  allowed_branch_id: number[];
  allowed_branch_id_expiration_date_time?: {
    [key: number]: string;
  };
  allowed_franchise_id?: number[];
  allowed_merchant_id?: number[];
  cms_shortcuts: string[];
  cms_start_page: string;
  previous_login: string;
  date_of_birth: string;
  email: string;
  item_id: number;
  name: string;
  roles: string[];
  salutation: string;
  status: number;
  photo: string;
  telephone: string;
  last_login: string;
  language: number;
}

export interface OrderWithpageNumber {
  [key: number]: Order;
}

export interface Role {
  id: number;
  label: string;
  description: string;
}

export type OrderStatusTypes =
  | "Nieuw"
  | "Keuken"
  | "Onderweg"
  | "Afgeleverd"
  | "ReadyForDelivery"
  | "Canceled";

export interface LayoutShortcut {
  name: string;
  href: string;
}

export interface LayoutBranch {
  id: number;
  name: string;
}

export interface SidebarLayerItem {
  /** Last url segment, e.g. "general" for /websitesettings/general. An empty
   * key links to the basePath itself (the default page of the layer). */
  key: string;
  text: string;
  description?: string;
  icon: IconsVariant;
  hideInTurkiye?: boolean;
}

export interface SidebarLayerGroup {
  title?: string;
  items: SidebarLayerItem[];
}

/** A second sidebar level that replaces the main menu while one of its
 * routes is open. */
export interface SidebarLayer {
  title: string;
  basePath: string;
  /** data-tour id, shared by the sidebar layer and the mobile tabs */
  tourId?: string;
  groups: SidebarLayerGroup[];
}

export interface LayoutRoute {
  text: string;
  href: string;
  icon: IconsVariant;
  color: string;
  role?: string;
  items?: {
    text: string;
    link: string;
    role?: string;
    layer?: SidebarLayer;
  }[];
}

export interface LayoutDataInterface {
  shortcuts: LayoutShortcut[];
  branches: LayoutBranch[];
  routes: LayoutRoute[];
}

export interface DeliveryMethod {
  is_active?: boolean;
  in_use?: boolean;
  is_not_active_description: string;
  item_id: number;
  delivery_method_id: number;
  name: string;
  position: string;
  payment_method_id?: number;
  min_value?: number[];
  transaction_cost_fixed_price?: number[];
  transaction_cost_percentage_price?: number[];
  payment_method_description?: string;
  transaction_cost_description?: string;
  free_from?: number;
  transaction_cost_max: number;
  transaction_cost_min: number;
}

export interface Postalcode {
  id: string;
  created: boolean;
  zipcode_range_begin: string;
  zipcode_range_end: string;
  order_value: number[];
  delivery_fee: number[];
  average_delivery_time: number;
  area?: string;
  description: string;
  is_active: boolean;
  country: string;
  in_use: boolean;
}

export interface OpeningTimes {
  item_id?: number;
  delivery_method: number;
  is_active: boolean;
  is_asap_active: boolean;
  is_quota_active?: boolean;
  is_preorder_allowed?: boolean;
  preorder_value?: string;
  preorder_value_type?: string;
  minimum_delivery_time: number;
  is_exception?: boolean;
  time_periods: TimePeriod[];
  is_custom_time_slot_active?: boolean;
  custom_time_slot_label?: string;
  use_time_difference_in_time_slots?: boolean;
  quota_type?: number;
  is_asap_only?: boolean;
}

export interface TimePeriod {
  start_date?: string;
  end_date?: string;
  is_active: boolean;
  message?: string;
  quota?: number;
  time_interval?: number;
  time_period_units: Partial<TimePeriodUnit>[];
  is_exception?: boolean;
  item_id?: number;
}

export interface TimePeriodUnit {
  start_time: string[];
  end_time: string[];
  is_active: boolean;
  is_closed?: boolean;
  unit_type: string;
  unit_value: string;
  message?: string;
  quota: number;
  time_interval: number;
  item_id?: number;
  isChecked?: boolean;
  full?: boolean;
}

export interface BusinessDay {
  day: string;
  is_active: boolean;
  start_time: string;
  end_date: string;
  frequenty_amount: number;
  step_size: number;
  unit_type: string;
  unit_value: string;
  message: string;
  is_closed: boolean;
}

export interface BusinessSheets {
  sheets: BusinessHours[];
  custom: CustomBusinessHours[];
  is_active: boolean;
  hours: number;
  same_day: boolean;
}

export interface BusinessHours {
  name: string;
  id: number;
  delivery_time: number;
  is_active: boolean;
  step_increment: number;
  time_periods: BusinessDay[];
}

export interface CustomBusinessHours {
  from: string;
  till: string;
  id: string;
  status: string;
  reason: string;
}

export interface ReviewSettings {
  item_id: number;
  automatic_favorite_average_setting: number;
  automatic_favorite_delivery_setting: number;
  automatic_favorite_quality_setting: number;
  automatic_favorite_service_setting: number;
  automatic_non_visible_average_setting: number;
  automatic_non_visible_delivery_setting: number;
  automatic_non_visible_quality_setting: number;
  automatic_non_visible_service_setting: number;
  delivery_rating_active: boolean;
  delivery_rating_show_in_website: boolean;
  internal_notification_active: boolean;
  internal_notification_address: string;
  internal_notification_method: number;
  is_active: boolean;
  has_internal_notification_address: boolean;
  product_reviews_enabled: boolean;
  quality_rating_active: boolean;
  quality_rating_show_in_website: boolean;
  review_email_text_setting: {};
  review_page_text_setting: {};
  review_replies_show_in_website: boolean;
  google_reviews_show_in_website: boolean;
  review_reply_notify_user: boolean;
  service_rating_active: boolean;
  service_rating_show_in_website: boolean;
  is_review_accepted_automatically: boolean;
}

interface ProductReview {
  average_rating: number;
  comments: string[];
  review_count: number;
}
export interface ProductReviews {
  [productName: string]: ProductReview;
}

export interface Branch {
  id: number;
  description?: string;
  is_active: boolean | string;
  is_not_active_description?: any;
  name: string;
  place: string;
  start_date?: string;
  telephone_number: string;
  telephone_country_prefix: string;
  phone?: string;
  street_name: string;
  house_number: number;
  house_number_extension: string;
  kvk: string;
  btw: string;
  zip_code: string;
  lat: number;
  gps_longitude: number;
  gps_latitude: number;
  lon: number;
  email: string;
  image_cloudinary_id?: string;
  item_id: number;
  company_vat_number?: string;
  chamber_of_commerce_registration_number?: string;
}

export interface MerchantResponse {
  id: number;
  is_active: boolean;
  company_name: string;
}

export enum KitchenTypeEnum {
  HALAL = 1,
  AFGHAN = 2,
  AFRICAN = 3,
  AMERICAN = 4,
  AMERICAN_PIZZA = 5,
  OTHER = 6,
  ANTILLEAN = 7,
  ASIAN = 8,
  BALKAN_CUISINE = 9,
  ORGANIC = 10,
  BRAZILIAN = 11,
  SANDWICHES = 12,
  BUBBLE_TEA = 13,
  BURGERS = 14,
  CHINESE = 15,
  CURRY = 16,
  DONER = 17,
  EGYPTIAN = 18,
  FALAFEL = 19,
  HEALTHY = 20,
  GLUTEN_FREE = 21,
  GREEK = 22,
  GRILL = 23,
  GYROS = 24,
  HOT_DOGS = 25,
  HOMEMADE = 26,
  ICE_CREAM = 27,
  INDIAN = 28,
  INDONESIAN = 29,
  ITALIAN = 30,
  ITALIAN_PIZZA = 31,
  JAPANESE = 32,
  VEAL = 33,
  KAPSALON = 34,
  CHICKEN = 35,
  COFFEE = 36,
  KOREAN = 37,
  LEBANESE = 38,
  LUNCH = 39,
  MOROCCAN = 40,
  MEXICAN = 41,
  DESSERTS = 42,
  DUTCH = 43,
  NOODLES = 44,
  BREAKFAST = 45,
  PANCAKES = 46,
  PASTA = 47,
  FRIES = 48,
  POKE_BOWL = 49,
  PORTUGUESE = 50,
  ROTI = 51,
  BEEF = 52,
  SALADS = 53,
  SHOARMA = 54,
  SMOOTHIES_JUICES = 55,
  SNACKS = 56,
  SOUPS = 57,
  SPANISH_TAPAS = 58,
  SPARE_RIBS = 59,
  STEAKS = 60,
  SURINAMESE = 61,
  SUSHI = 62,
  SYRIAN = 63,
  CAKE = 64,
  TACOS = 65,
  TANDOORI = 66,
  THAI = 67,
  TURKISH = 68,
  TURKISH_PIZZA = 69,
  PORK = 70,
  VEGETARIAN = 71,
  VIETNAMESE = 72,
  FISH = 73,
  WOK = 74,
  WRAPS = 75,
  SEAFOOD = 76,
  VEGAN = 77,
  SCHNITZEL = 78,
  A_LA_CARTE = 79,
  STAMPPOT = 80,
  FRENCH = 81,
  PERSIAN = 82,
  ARGENTINIAN = 83,
}

export interface Franchise {
  id: number;
  is_active: boolean;
  name: string;
  country?: number;
  languages?: number[];
  kitchen_types?: KitchenTypeEnum[];
}

export interface AppMerchant extends Franchise {}

export interface BranchGroup {
  branch: Branch;
  franchise?: Franchise;
  merchant?: AppMerchant;
}

export interface LayoutData {
  branches: BranchGroup[];
  start_page: string;
  shortcuts: string[];
}

export type FilterOperationTypes =
  | "between"
  | "greater_than"
  | "less_than"
  | "equals"
  | "not_equals"
  | "sort"
  | "in"
  | "contains"
  | "not_in"
  | "greater_or_equal_than"
  | "desc_numeric"
  | "asc_numeric"
  | "in_and_not_in";

export interface Filter {
  type: string;
  operation: FilterOperationTypes;
  index?: number[];
  values: number[];
}

export interface ReviewRating {
  1: string;
  2: string;
  3: string;
  4: string;
}

export interface ReviewProduct {
  id: number;
  rating: number;
  comment: string;
  name: string;
}

export interface Review {
  item_id: number;
  time_stamp: string;
  order_id?: string;
  public_order_number?: string;
  rating?: ReviewRating;
  is_active: boolean;
  is_marked_as_favorite: boolean;
  is_removed: boolean;
  is_visible: boolean;
  is_read: boolean;
  originator: string;
  product_comment: { [productId: string]: string } | string;
  product_name: { [productId: string]: string } | string;
  product_rating: { [productId: string]: string } | string;
  rating_description: string;
  rating_description_response: string;
}

export interface ReviewDiscountCode {
  name: string;
  description: string;
  end_date: string;
  code: string;
}

export interface LooseObject {
  [key: string]: any;
}

export interface TableFilterFormat {
  table?: string;
  type?: string | string[];
  id: string;
  operation: FilterOperationTypes;
  forceOperation?: FilterOperationTypes;
  value?: any;
  index?: string[];
  conditional?: (value: any) => any;
}

export interface Customer {
  stats?: Statisctic[];
  website_account_id: InnerValue<any> | string;
  name: InnerValue<any> | string;
  first_name: InnerValue<any> | string;
  last_name: InnerValue<any> | string;
  company_name: InnerValue<any> | string;
  company_vat_number: InnerValue<any> | string;
  street_name: InnerValue<any> | string;
  house_number: InnerValue<any> | string;
  house_number_extension: InnerValue<any> | string;
  zip_code: InnerValue<any> | string;
  city: InnerValue<any> | string;
  telephone: InnerValue<any> | string;
  email: InnerValue<any> | string;
  branch_id: InnerValue<any> | string;
  status: InnerValue<any> | string;
  hash_crc32: InnerValue<any> | string;
  item_id: InnerValue<any> | string;
}

export interface Statisctic {
  source: string;
  total: string;
  quantity: string;
  delivery_method: string;
  payment_method: string;
  orders: string;
}

export interface BaseFilterProps {
  pageNumber?: number;
  pageSize?: number;
  filters?: any;
}

export interface PieChart {
  delivery_methods: {
    TAKEAWAY: string;
    DELIVERY: string;
    DINE_IN: string;
  };
  payment_methods: {
    IDEAL: string;
    CASH: string;
    CREDITCARD: string;
    CASH_NOT_EXACT: string;
    DEBITCARD: string;
    CREDITCARD_POS: string;
    INVOICE_LATER: string;
    BANCONTACT: string;
    PAYCONIQ: string;
    DEBITCARD_AT_DELIVERY: string;
    PAYPAL: string;
    VISA: string;
    MASTERCARD: string;
    ONLINE_BANK_TRANSFER: string;
    EXPRESS_PAY: string;
    PAYCONIQ_QR_CODE: string;
    ANDERS: string;
    CHEQUE: string;
    VOUCHER: string;
    GIFT_CARD: string;
  };
  source: {
    WEBSITE: string;
    THUISBEZORGD_NL: string;
    UBER_EATS: string;
    HOMIE: string;
    IOS_WEB_APP: string;
    ANDROID_WEB_APP: string;
    POS: string;
  };
}

export interface DeliverFees {
  amount: string;
  cost: string;
}

export interface ZipcodesTableDataRequest {
  color_code: string;
  average_delivery_time: string;
  country: number;
  delivery_fee: string[];
  order_value: string[];
  free_from?: string;
  zipcode_range_begin: string;
  zipcode_range_end: string;
  temporary_free_delivery_cost: boolean;
  in_use: boolean;
  area: string[];
  is_active: boolean;
  not_active_description?: string;
  item_id: number;
  h3_indexes: string[];
}

export interface Revenue {
  Month: string;
  Week: string;
  Day: string;
  App: number;
  ["date "]: string;
  WEBSITE: number;
  THUISBEZORGD_NL: number;
  APP_ALL: number;
  OWN_CHANNEL_ALL: number;
  OUT_OF_DB: number;
  POS: number;
  ALL: number;
  WEBSITE_AND_APP: number;
  UBER_EATS: number;
}

export interface Merchant {
  accountantcy_system_id: string;
  name: string;
  invoice_relation_contact_person: string;
  company_name: string;
  company_address: string;
  house_number: number;
  house_number_extension: string;
  company_zipcode: string;
  company_city: string;
  company_country: string;
  relation_contact_telephone: string;
  relation_contact_mobile: string;
  billing_email: string;
  relation_contact_email: string;
  account_email: string;
  iban: string;
  bic: string;
  value?: string;
  label?: string;
}

export interface User {
  restaurant: string;
  name: string;
  phone: string;
  email: string;
}
export interface IThemeItem {
  item_id: number;
  product_id: string;
  template_name: string;
  themeType?: string;
  theme: {
    alternate_background_color: string;
    alternate_background_labels_text_color: string;
    background_color: string;
    background_labels_text_color: string;
    button_background_color: string;
    button_text_color: string;
    card_background_color: string;
    card_text_color: string;
    category_background_color: string;
    category_text_color: string;
    footer_background_color: string;
    footer_text_color: string;
    initial_message_box_color: string;
    initial_message_box_color_text_color: string;
    nav_background_color: string;
    nav_text_color: string;
    recommendation_card_background_color: string;
    recommendation_card_text_color: string;
    restaurant_closed_message_box_color: string;
    restaurant_closed_message_box_text_color: string;
    restaurant_emergency_break_message_box_color: string;
    restaurant_emergency_break_message_box_text_color: string;
    restaurant_open_message_box_color: string;
    restaurant_open_message_box_text_color: string;
    order_now_button_color: string;
    order_now_button_text_color: string;
    order_now_button_in_navigation_color: string;
    order_now_button_in_navigation_text_color: string;
  };
}
export interface IThemeData {
  default: IThemeItem[];
  custom: IThemeItem[];
}
export interface IWebsiteSettings {
  logo?: string;
  header_background_image?: string;
  home_sliders?: string[];
  sections: string[];
  payment_method_position: number[];
  delivery_method_position: number[];
  item_id: number;
  color_template_id: number;
}

export interface IUserSettings {
  is_order_accepted_automatically: boolean;
  is_pos_order_accepted_automatically?: boolean;
  item_id?: number;
  play_sound: boolean;
  play_sound_times: number;
  show_only_new_orders: boolean;
  is_active: boolean;
  alarm_volume?: number;
  extra_alarm_minute_delay?: number;
  extra_alarm_sound_times?: number;
  order_confirm_asap_minutes_buttons_is_active?: boolean;
  sticky_pos_order_screen?: boolean;
  lanes?: string[];
  alarm_sound_file_choice?: AlarmFileSounds;
  is_tip_shown_in_live_tracker?: boolean;
  is_pos_order_printed_automatically?: boolean;
  sources: number[];
  delivery_methods: number[];
  show_order_count: boolean;
}
export interface ITrackerSettings {
  is_food_tracker_active: boolean;
  time_to_next_day: string;
  is_tip_shown_in_live_tracker?: boolean;
  use_kitchen_screen: boolean;
  use_formulas: boolean;
  use_packaging_screen: boolean;
  icon_cloudinary_image_id: string;
  receipt_branch_ids: number[];
}
export interface PrinterSettingsResponse {
  printers: string[];
  configuration_names: string[];
  chosen_printers: string[];
  print_quantities: number[];
  receipt_setting_ids: number[];
  is_active: boolean[];
  branch_id: number[];
  item_id: number[];
}
export interface PrinterSettingsData {
  chosenPrinters: string[];
  printers: {
    [key: string]: {
      quantity: number;
      receiptId: number;
      configName: string;
    }[];
  };
  isActive: boolean;
  branchId: number;
  itemId: number;
}

export interface IReceipt {
  format: string;
  html: string;
  order_id: number;
  printer_name: string;
  quantity: number;
  margin_top: number;
  margin_bottom: number;
  margin_left: number;
  margin_right: number;
}

export interface IDeliveryMethod {
  delivery_method_id: number;
  in_use: boolean;
  is_active: boolean;
  is_not_active_description?: string;
  item_id: number;
  emergency_break_active?: boolean;
  emergency_break_message?: string;
  emergency_break_minutes?: number;
  news_message?: string;
}

export interface Jwt {
  refresh_token: string;
  token: string;
  last_login: string;
}

export interface TimeSlot {
  date: string;
  is_active: boolean;
  message: {
    all: string;
  };
  is_asap_active: boolean;
  time_slots: TimeSlotUnit[];
}
export interface TimeSlotUnit {
  start_date: string;
  end: string;
  is_active: boolean;
  is_closed: boolean;
  quota: number;
  quota_left: number;
  start: string;
  start_time: string;
  end_time: string;
}

export interface OrderUser {
  name: string;
  telephone: string;
  email: string;
  street_name: string;
  house_number: number;
  house_number_extension: string;
  zip_code: string;
  city: string;
  branch_id: number;
  item_id?: number;
  day?: string;
  time?: string | string[];
  area?: string;
  company_name?: string;
  apartment_number?: number;
  district?: string;
  avenue?: string;
  neighborhood?: string;
}

export interface Discount {
  item_id?: number;
  apply_on_hand_over_time: boolean;
  is_active: boolean;
  discount_type: number;
  pos_integration_code: string;
  name: string;
  description: string;
  combine_with_other_discounts: boolean;
  allowed_delivery_methods: number[];
  allowed_payment_methods: number[];
  allowed_sources: number[];
  discount_method: number;
  discount_total_price_calculation_method: number;
  discount_calculation_base_method: number;
  base_method_minimum_to_apply: number;
  base_method_maximum_apply_amount: number;
  base_method_apply_interval: number;
  discount_value: string;
  discount_calculation_apply_method: number;
  apply_on_menu: boolean;
  not_apply_on_item_ids: number[] | number[][];
  not_apply_on_items_from_cat?: number[][];
  free_menu_item_ids: number[] | any;
  custom_base_method_apply_array: number[];
  time_periods?: TimePeriod;
  priority?: number;
  apply_on_menu_item_ids: number[];
  apply_on_placed_order_time?: boolean;
  discount_code_value?: string;
  is_always_visible: boolean;
  is_highlighted: boolean;
  vat_type?: number;
}

export interface DataValuaStructure<T = any> {
  value: T;
  created: string;
}

export interface DataStructure<T = any> {
  all: DataValuaStructure<T>;
}

export type DataProperty<T = any> = DataStructure<T>[] | DataValuaStructure<T>;
export interface AppIcon {
  src: string;
  sizes: string;
  type: string;
  purpose: string;
}
export interface ApplicationSettings {
  short_name: string;
  item_id: number;
  theme_color: string;
  background_color: string;
  icon_large?: string;
  icon_latest_url?: string;
}

export interface CartValidatorResponse {
  discount_code_applied: boolean;
  discount_code_minimum_to_apply: string;
  discount_code_validation: boolean;
  name: string;
  description: string;
  quantity: number;
  single_price_inc_vat: number;
  type: number;
  vat_percentage: number;
  discounts: {
    description: string;
    name: string;
    single_price_inc_vat: number;
    type: number;
    vat_percentage: number;
  }[];
  minimal_order_value_reached?: boolean;
  minimum_order_value?: string;
  minimum_for_free_delivery?: string;
  delivery_fee?: string;
  zip_code?: string;
  validate: any[];
}
export interface ReceiptsDataResponse {
  receipt_type: keyof typeof ReceiptEnum;
  branch_info_position: number;
  category_name_position: number;
  category_display_order: string[];
  description: string;
  font_family: string;
  font_size: string;
  item_id: number;
  margin_bottom: number;
  margin_left: number;
  margin_right: number;
  margin_top: number;
  name: string;
  width: string;
  type?: string;
  logo_exist: boolean;
  branch_info_exist: boolean;
  order_info_exist: boolean;
  order_user_info_exist: boolean;
  qr_exist: boolean;
  qr_position: number;
  information_alignment: number;
  sources: any[];
  note: string;
  note_font_family: string;
  note_font_size: string;
  note_text_color: string;
  tip_exist?: boolean;
  show_category_name?: boolean;
  show_prices?: boolean;
  information_positions?: number[];
  is_toppings_separated?: boolean;
  show_topping_prices?: boolean;
  show_vat?: boolean;
  apply_on_menu?: boolean;
  not_apply_on_item_ids?: number[];
  apply_on_item_ids?: number[];
  show_daily_receipt_number?: boolean;
  targets?: number[];
  show_hand_over_time_when_asap: boolean;
  order_remarks_font_weight: string;
  language: string;
  logo_position: number;
  show_extra_prices: boolean;
  show_external_logo: boolean;
  is_customer_receipt: boolean;
  show_payment_qr?: boolean;
}

export interface PushBody {
  send_timestamp: string;
  notification_campaign_title: string;
  notification_campaign_description: string;
  body: {
    title: string;
    type: number;
    body: string;
    data: any;
    actions?: { title: string; action: string }[];
    image?: string;
  };
}

export interface OrderIntakeMailResponse {
  is_active: boolean;
  order_intake_email_index?: string[];
  template_reference_id: number;
  show_category_name: boolean;
  show_product_name: boolean;
  show_product_code: boolean;
  skip_pos_orders: boolean;
  item_id: number;
}
export interface OrderConfirmationMailResponse {
  branch_id: number;
  custom_message: string;
  item_id: number;
  mail_subject: string;
  please_call_here_message: string;
  template_reference_id: number;
  welcome_message: string;
  your_order_has_been_received_message: string;
  show_btw: boolean;
}

export interface LoyaltySettings {
  loyalty_settings: {
    max_spendable_amount: number;
    item_id: number;
    is_active: boolean;
  };
  loyalty_discount: Discount;
}

export interface InvoiceProduct {
  quantity: number;
  name: string;
  price_ex_vat: number;
  vat_percentage: number;
  total_ex_vat: string;
  total_vat: string;
  total: string;
}

export interface Invoice {
  invoice_number: number;
  date: string;
  total_ex_vat: number;
  total_vat: string;
  total: string;
  unpaid: string;
  status: string;
  pdf_url: string;
  products: InvoiceProduct[];
}

export interface MenuStatusesResponse {
  [productId: string]: string;
}
export interface RecommendProduct {
  category_id: number;
  name: string;
  product_id: number;
  old_recommended?: {
    name: string;
    product_id: number;
  }[];
  recommended: {
    name: string;
    product_id: number;
  }[];
}
export interface RecommendCategory {
  category_id: number;
  name: string;
  products: RecommendProduct[];
}

export interface Notification {
  notification: {
    body: string;
    bulk_status: number;
    expiration_timestamp: string;
    franchise_id: number;
    item_id: number;
    notification_campaign_description: string;
    notification_campaign_title: string;
    notification_category: number;
    notification_type: number;
    originator: string;
    retry_count: number;
    retry_frequency: number;
    send_timestamp: string;
    status: number;
    validity: string;
  };
  stats: {
    could_not_send: string;
    item_id: number;
    notification_id: string;
    removed: string;
    sent: string;
    type: string;
  };
}

export type ColoredLabelVariants = "success" | "error" | "warning" | "info";

export interface CmsUserVariants {
  id: number;
  translationKey: string;
  variant: ColoredLabelVariants;
}

export interface RolesResponse {
  allowed_franchises: number[];
  allowed_merchants: number[];
  roles: string[];
}

export interface BranchData {
  name: string;
  is_active: boolean;
  description: string;
  image_cloudinary_id: string;
  street_name: string;
  house_number: string;
  house_number_extension: string;
  zip_code: string;
  place: string;
  gps_latitude: number;
  gps_longitude: number;
  email: string;
  telephone_number: string;
  telephone_country_prefix: string;
  company_vat_number?: string;
  chamber_of_commerce_registration_number?: string;
}

export interface MerchantData {
  company_name: string;
  invoice_relation_contact_person: string;
  company_address: string;
  company_zipcode: string;
  company_city: string;
  company_country: string;
  relation_contact_telephone: string;
  relation_contact_mobile: string;
  relation_contact_email: string;
  user: MerchantUser;
}

export enum CartItemStatus {
  pending = 1,
  in_preparation,
  ready,
  on_hold,
  cancelled,
}

export interface FranchiseData {
  name: string;
  country: number;
  product_enum: number;
  is_active?: boolean;
  item_id?: number;
  languages: number[];
  kitchen_types?: KitchenTypeEnum[];
}

export interface MerchantUser {
  admin_name: string;
  admin_telephone_prefix: string;
  admin_telephone: string;
  admin_account_email: string;
  password: string;
  is_franchise_owner: boolean;
}

export interface Tip {
  orderID: number;
  date: string;
  name: string;
  tip: string;
  courierName: string;
}

export interface TipData {
  date_begin: string;
  date_end: string;
  tips: Tip[];
}

export interface HostnameSetting {
  host_name: string[];
  is_active: string;
  item_id: any;
  product_id: number;
  template_id: number;
  template_version: number;
  apple_id: string;
  android_bundle_id: string;
}

export interface CloudinaryResponse {
  api_key: string;
  file_id: string;
  public_id: string;
  signature: string;
  timestamp: number;
  upload_url: string;
}

export interface CloudinaryUploadResponse {
  api_key: string;
  asset_id: string;
  bytes: number;
  created_at: string;
  etag: string;
  folder: string;
  format: string;
  height: number;
  original_filename: string;
  placeholder: false;
  public_id: string;
  resource_type: string;
  secure_url: string;
  signature: string;
  tags: any[];
  type: string;
  url: string;
  version: number;
  version_id: string;
  width: number;
}
export interface ErrorLog {
  error_info: string;
  error_json: string;
  item_id: number;
  source: string;
  time_stamp: string;
  url: string;
  franchise_id: string;
}
export interface ThuisbezorgdSettingData {
  is_active: boolean;
  user_name: string;
  password: string;
  restaurant_id: string;
}
export interface TrendyolGoSettingData {
  is_active: boolean;
  supplier_id: number;
  store_id: number;
  username: string;
  password: string;
}
export interface UberSettings {
  external_store_id: string;
  item_id: number;
  estimated_preparation_time: number;
}
export interface UberActivation {
  is_order_manager: boolean;
  require_manual_acceptance: boolean;
  integration_enabled: boolean;
}

export interface VersionLog {
  version_increment_type: number;
  extra_remark: string[];
  bug_fixes_description: string[];
  new_additions_description: string[];
  changes_and_improvements_description: string[];
  show_message: boolean;
  publish: boolean;
  expiration_date: string;
  item_id: number;
  version_number: string;
  version?: string;
}

export interface LoyaltyLedgers {
  loyalty_type: string;
  loyalty_mutation: number;
  loyalty_valid_from: string;
  loyalty_valid_until: string;
  loyalty_description: string;
  item_id: number;
}

export type VersionLogs = VersionLog[];

export interface SocialMediaSettingData {
  google_analytics_id: string;
  meta_pixel_id: string;
  meta_domain_verification_id: string;
  meta_conversions_api_token?: string;
  google_tag_manager_id: string;
  item_id?: number;
}

export interface FinancialReport {
  totals: {
    [key: string]: string;
  };
  vats: {
    [key: string]: {
      omzet: string;
      btw: string;
    };
  };
  sources: {
    [key: string]: {
      [key: string]: string;
    };
  };
  specifications: {
    [key: string]: {
      [key: string]: string;
    };
  };
}

export interface FinancialReportReceiptSettings {
  chosen_printer: string;
  print_quantity: number;
  day_type: number;
  automatic_print: boolean;
  width: string;
  margin_top: string;
  margin_bottom: string;
  margin_left: string;
  margin_right: string;
  font_family: string;
  font_size: string;
  item_id: number;
}
export interface FinancialReportReceipt {
  html: string;
  order_id: string;
  printer_name: string;
}

export interface Newsletter {
  email_address: string;
  status: string;
  activation_code: string;
  time_stamp: string;
  item_id: number;
}

export interface PosCommonSettings {
  voip_is_active: boolean;
  show_product_images: boolean;
  accept_all_addresses: boolean;
  accept_all_times_for_pos: boolean;
  reset_delivery_method: boolean;
  item_id: number;
}

export interface PosUserSettings {
  is_map_view?: boolean;
  is_cart_on_right: boolean;
  standard_delivery_method: any;
  color_palette: string[];
  item_id: number;
  standard_payment_method: any;
  font_size: number;
  category_card_left?: boolean;
  category_column_count: number;
  product_column_count: number;
  category_card_position: number;
  standard_get_menu_target: number;
  payment_method_options?: number[];
  column_count_per_category?: { [key: string]: number };
  delivery_method_options?: number[];
  is_always_ask_for_topping?: boolean;
  show_print_popup?: boolean;
  default_pos_terminal_id?: string | null;
}

export interface PosTerminal {
  pos_terminal_id: string;
  pos_terminal_name: string;
}

export type PosSettings = PosUserSettings & PosCommonSettings;

export interface KioskSettings {
  item_id?: number;
  home_sliders: string[];
  delivery_methods: number[];
  take_out_image: string;
  dine_in_image: string;
  theme_template_id: number;
  logo?: string;
  pos_terminal_id?: string;
  /** Write only: the GET never returns it. */
  pin_code?: string;
}

export interface VoipCurrent {
  external_status_id: string;
  telephone: number;
  users: VoipUser[];
}

export interface VoipUser {
  name: string;
  street_name: string;
  house_number: string;
  house_number_extension: string;
  zip_code: string;
  city: string;
  telephone: string;
  email: string;
  branch_id: number;
  status: number;
  hash_crc32: number;
  item_id: number;
}

export interface InternalNotification {
  message_type: number;
  message: string;
  is_read: boolean;
  timestamp: string;
  item_id: number;
}

export interface InternalNotifications {
  notifications: InternalNotification[];
  total_count: number;
}

export type NotificationType = keyof typeof InternalMessageType;

export type UnreadNotifications = Record<NotificationType, number>;

export interface ErrorData {
  branch_id: number;
  error_id: string;
  error_message: string;
}

export interface AppsmenError extends AxiosError<ErrorData> {}

export interface ZipcodeApi {
  city: string;
  location: any;
  municipality: string;
  number: number;
  postcode: string;
  province: string;
  street: string;
}

export interface ZipcodeByRadius {
  zipcode: "string";
  distance_meters: "string";
}

export interface AccountRoles {
  [key: string]: string[];
}

export interface NewsletterCampaigns {
  item_id?: string;
  subject: string;
  template_id?: string;
  title: string;
  text: string;
  image: string;
  send_timestamp: string;
  stats?: any;
  c_fit?: boolean;
  background_color: string;
  is_logo_shown: boolean;
}

export interface NewsletterCampaign {
  all: {
    email: string;
    external_id: string;
    item_id: number;
    send_time: string;
    status: string;
  }[];
  campaign: {
    body: string;
    subject: string;
    send_timestamp: string;
    source: string;
    title: string;
    text: string;
    c_fit: boolean;
    background_color: string;
    is_logo_shown: boolean;
    image: string;
  };
  stats: {
    [key: string]: number;
  };
}

export interface NewsletterRegisteredCount {
  opt_in_count: number;
}

export interface DiscountCodes {
  fixed: DiscountCode[];
  generated: DiscountCode[];
}
export interface DiscountCode {
  code: string;
  discount_id: number;
  start_date: string;
  end_date: string;
  limit: number;
  usage_left: number;
  code_type?: number;
  account_id?: number;
  item_id?: number;
}

export interface DiscountCodeGenerator {
  is_active: boolean;
  duration: number;
  generator_type: number;
  start_date: string;
  discount_id: number;
  name: string;
  item_id?: number;
}

export interface GeoLocation {
  branch_id: number;
  account_id: number;
  order_id: string;
  lat: string;
  lon: string;
  time_stamp: string;
}

export interface Deposit {
  [key: string]: string;
}

export interface DepositRow {
  date: string;
  value: string;
}

export interface Schedule {
  start_date_and_time: string;
  end_date_and_time: string;
  break_in_minutes: string;
  money_at_start_of_day: string;
  name?: string;
  item_id: number;
  account_id: string;
  is_paid?: boolean;
  partial_deposit: Deposit;
  hourly_wage?: number;
  hours?: string;
}

export interface TotalLoyaltyPointsResponse {
  currency_value: number;
  total_points: number;
}

export interface LoyaltyPointsRequest {
  account_id: number;
  loyalty_type: string;
  loyalty_mutation: number;
  loyalty_valid_from: string;
  loyalty_valid_until: string;
  loyalty_description: string;
}

export interface TotalLoyaltyPointsResponse {
  currency_value: number;
  total_points: number;
}

export interface SoldProducts {
  begin_date: string;
  end_date: string;
  total_sold: number;
  sold: { quantity: number; name: string }[];
  unsold: string[];
}

export type MenuItemImageType =
  | "item-image"
  | "extend-image"
  | "extend-video"
  | "slider-image";

export interface Vat {
  id: number;
  value: string;
  label: string;
  percentage: string;
  country: number;
  percentageValue: number;
}
export interface VatBasic {
  id: number;
  label: string;
  percentage: number;
}

export interface RestaurantMap {
  item_id: number;
  name: string;
  is_active: boolean;
  tables: RestaurantTable[];
  background_color?: string;
}

export interface RestaurantTable {
  order_id?: number;
  amount_of_seats: number;
  table_number: number;
  item_id: number;
  name: string;
  x: string;
  y: string;
  width: number;
  height: number;
  color: string;
  mapId?: number;
  number_of_seats_left?: number;
  number_of_seats_above?: number;
  number_of_seats_right?: number;
  number_of_seats_below?: number;
}

export interface QrSettings {
  layout_type: number;
  sort_option: number;
  statuses: number[];
  branch_id: number;
  map_ids: number[];
}

export interface BranchOnboarding {
  branch: Partial<Branch>;
  menu_complete?: boolean;
  opening_hours_complete?: boolean;
  zipcodes_complete?: boolean;
  delivery_methods_complete?: boolean;
  payment_methods_complete?: boolean;
  website_settings_complete?: boolean;
  app_settings_complete?: boolean;
  uber_eats_complete?: boolean;
  thuisbezorgd_complete?: boolean;
  voip_complete?: boolean;
  homie_complete?: boolean;
  folder_complete?: boolean;
}

export enum OnboardingActivityType {
  MENU = 1,
  EXTRAS = 2,
  OPENING_TIMES = 3,
  ZIPCODES = 4,
  DISCOUNTS = 5,
  PAY_ACCOUNT = 6,
  WEBSITE = 7,
  KASSA_INTEGRATION = 8,
  FLYER = 9,
  GOOGLE_PAGE = 10,
  INVOICING = 11,

  THUISBEZORGD = 100,
  UBER = 101,
  HOMIE = 102,
}

export enum OnboardingActivityStatus {
  ACTIVATED = "1",
  PENDING = "2",
  REJECTED = "3",
  IN_PROGRESS = "4",
}

export interface ActivityHistory {
  status: number;
  message: string;
  user: number;
  time_stamp: string;
}

export interface OnboardingActivity {
  activation_type: OnboardingActivityType;
  activation_status: OnboardingActivityStatus;
  status_history: number[];
  system_message: string[];
  item_id: number;
  activity_history: ActivityHistory[];
}

export interface Onboarding {
  activities: OnboardingActivity[];
  branch_id: number;
  start_date: string;
  branch_name: string;
}

export interface Flyer {
  name: string;
  flyer_pdf_public: string;
  flyer_pdf_private: string;
  external_order_number: string;
  payment_status: number;
  approved_by: string;
  is_approved: boolean;
  item_id: number;
  price: string;
}

export interface DownloadHub {
  item_id: number;
  url: string;
  name: string;
}

export type SplitPaymentTypes = "equal" | "custom" | "cart";

export interface PaymentLedger {
  is_paid: boolean;
  is_removed: boolean;
  item_id: number;
  payment_amount: string;
  write_account_id: { [key: number]: number };
  payment_tip?: string;
  payment_service_fee?: string;
  payment_method_id?: number;
  transaction_email?: string;
  transaction_url?: string;
}

export interface PaymentLedgerPaymentBody {
  payment_service_fee: string;
  payment_tip: string;
  transaction_email?: string;
  transaction_terminal_code?: string;
}

export type CalendarUnitType =
  | CalendarUnitEnum.CALENDAR_DAY
  | CalendarUnitEnum.WEEK
  | CalendarUnitEnum.MONTH
  | CalendarUnitEnum.QUARTER
  | CalendarUnitEnum.YEAR;

export type PaymentMethodIdType =
  (typeof PaymentMethodEnum)[keyof typeof PaymentMethodEnum];
export type DeliveryMethodIdType =
  (typeof DeliveryMethods)[keyof typeof DeliveryMethods];

export interface ZipcodeStats {
  zip_code: string;
  total: number;
  average: number;
  quantity: number;
}
export interface PosDeliveryMethodsProps {
  translationKey: string;
  value: number;
  icon?: IconsVariant;
}
export interface LanguagesProps {
  value: string;
  icon?: IconsVariant;
  label: string;
  text?: string;
}

export interface SplitPaymentStates {
  splitCount: number;
  splitType: "custom" | "cart";
  paymentLedgers?: PaymentLedger[];
  activeIndex: number;
}

export interface SelectedCartItem {
  item_id: number;
  quantity: number;
  ledgerId: number;
  price: string;
}

export interface Coordinates {
  latitude: number;
  longitude: number;
}

// Franchise module
export type FranchiseKitchenType =
  | "pizza"
  | "sushi"
  | "burger"
  | "grill"
  | "kebab"
  | "asian"
  | "other";

export interface FranchiseOpeningTime {
  day: number; // 0 = monday ... 6 = sunday
  open: string; // "HH:mm"
  close: string; // "HH:mm"
  is_closed: boolean;
}

export interface FranchiseBranch {
  id: number;
  name: string;
  street_name: string;
  house_number: string;
  zip_code: string;
  place: string;
  telephone_number: string;
  email: string;
  kitchen_types: FranchiseKitchenType;
  is_active: boolean;
  is_online: boolean;
  lat: number;
  lon: number;
  // PC4 postcode areas served by this branch
  postal_codes: string[];
  opening_times: FranchiseOpeningTime[];
}

export type FranchisePaymentMethod = "cash" | "ideal" | "creditcard" | "pin";

export type FranchiseDeliveryStatus = "waiting" | "en_route" | "delivered";

export interface FranchiseOrder {
  id: number;
  public_order_number: string;
  branch_id: number;
  branch_name: string;
  created_at: string;
  total: number;
  status: number; // OrderStatus
  delivery_method: number; // 1 delivery, 2 takeaway, 3 dine in, 4 pos
  payment_method: FranchisePaymentMethod;
  customer_name: string;
  street: string;
  zip_code: string;
  place: string;
  lat?: number;
  lon?: number;
  courier_id?: string;
}

export interface FranchiseOrdersFilter {
  branchIds?: number[];
  dateFrom: string; // YYYY-MM-DD
  dateTo: string; // YYYY-MM-DD
  statuses?: number[];
  deliveryMethods?: number[];
  paymentMethods?: FranchisePaymentMethod[];
}

export interface FranchiseCourier {
  id: string;
  name: string;
  branch_id: number;
  lat: number;
  lon: number;
  order_id?: number;
  updated_at: string;
}

export interface FranchiseReview {
  id: number;
  branch_id: number;
  branch_name: string;
  originator: string;
  public_order_number: string;
  rating: number;
  quality: number;
  service: number;
  delivery: number;
  comment: string;
  created_at: string;
}

export interface FranchiseReviewsFilter {
  branchIds?: number[];
  dateFrom: string;
  dateTo: string;
  // Rounded star ratings (1-5) to include; empty means all.
  ratings?: number[];
  // Period to compare ratings with; defaults to the same length before.
  previousDateFrom?: string;
  previousDateTo?: string;
}

export interface FranchiseBranchRating {
  branch_id: number;
  branch_name: string;
  average: number;
  count: number;
  previous_average: number;
  previous_count: number;
}

export type FranchiseRevenueGranularity = "hour" | "day" | "month";

export interface FranchiseRevenuePoint {
  label: string;
  per_branch: Record<number, number>;
  total: number;
}

export interface FranchiseBranchRevenue {
  branch_id: number;
  branch_name: string;
  revenue: number;
  orders: number;
  average_order_value: number;
}

export interface FranchiseRevenue {
  granularity: FranchiseRevenueGranularity;
  timeline: FranchiseRevenuePoint[];
  branches: FranchiseBranchRevenue[];
  total_revenue: number;
  total_orders: number;
}

export interface FranchiseOverview {
  total_revenue: number;
  total_orders: number;
  average_order_value: number;
  previous_total_revenue: number;
  previous_total_orders: number;
  previous_average_order_value: number;
  active_branches: number;
  total_branches: number;
  online_branches: number;
  offline_branches: number;
  average_rating: number;
  review_count: number;
}

export enum MenuDataTagKind {
  CATEGORY = 1,
  PRODUCT = 2,
  TOPPING_CATEGORY = 3,
  TOPPING = 4,
  INGREDIENT = 7,
  SIZE = 8,
  VARIANT = 9,
}

export enum VerdictOption {
  /** A recognisable dish, drink, snack, sauce or ingredient that looks the same wherever it is sold. */
  DISH = 1,
  /** A branded product: customers expect the real product, so it gets no generated image. */
  BRAND = 2,
  /** A fantasy or house name whose contents can not be told from its name, description or ingredients. */
  UNCLEAR = 3,
  /** A section or option that is no food, such as aanbiedingen, deals or bezorgkosten. */
  NOT_FOOD = 4,
}

// "all" is the index, the other keys are language aliases ( en, nl, ... )
export interface MenuDataTagTranslations {
  all: string;
  [language: string]: string;
}

export interface MenuDataTagBody {
  slug: string;
  kind: MenuDataTagKind[];
  name: MenuDataTagTranslations;
  parent_tag_id: number | null;
  synonyms?: string[];
  description?: string;
  ingredient_tag_ids?: number[];
  allergens?: number[];
  kitchen_types?: KitchenTypeEnum[];
  image_verdict?: VerdictOption | null;
  image_subject?: string;
  image_subject_reason?: string;
}

export interface MenuDataTag extends MenuDataTagBody {
  item_id: number;
}

// A tag as the all endpoint returns it, only what is needed to show and pick it
export type MenuDataTagCompact = Pick<
  MenuDataTag,
  "item_id" | "slug" | "name" | "kind"
>;

export interface MenuDataTagsResponse {
  total: number;
  limit: number;
  offset: number;
  items: MenuDataTag[];
}

export interface MenuDataTagInUseErrorData extends ErrorData {
  error_id: "menuDataTagInUse";
  menu_item_count: number;
}

// The merged tags are deleted, everything that pointed at them points at into afterwards
export interface MenuDataTagMergeBody {
  tag_ids: number[];
  into: number;
}

export interface MenuDataTagMergeTagResult {
  tag_id: number;
  names_moved: number;
  menu_items_moved: number;
  branches: number;
  tag_references_moved: number;
  kinds_added: number;
  synonyms_added: string[];
}

export interface MenuDataTagMergeResponse extends Omit<
  MenuDataTagMergeTagResult,
  "tag_id"
> {
  merged_into: number;
  merged_tag_ids: number[];
  per_tag: MenuDataTagMergeTagResult[];
}

export interface MenuDataTagRemoveFromMenuItemsResponse {
  menu_item_count: number;
  branch_count: number;
}

// same_name: a name, synonym or slug of one is a spelling of the other, spelling: the slugs almost match,
// joined: the words are split differently. Strongest first
export type MenuDataTagMergeSuggestionReason = "same_name" | "spelling" | "joined";

export interface MenuDataTagMergeSuggestionTag {
  item_id: number;
  slug: string;
  name: string;
  name_count: number;
  menu_item_count: number;
}

// source is merged into target, the target is the most used of the two
export interface MenuDataTagMergeSuggestion {
  reason: MenuDataTagMergeSuggestionReason;
  detail: string;
  source: MenuDataTagMergeSuggestionTag;
  target: MenuDataTagMergeSuggestionTag;
}

// description per language code ( nl, en ... ), Dutch first
export interface MenuDescriptionsItem {
  item_id: number;
  category: string;
  name: string;
  description: Record<string, string>;
}

// products is how many had no description, failed_item_ids got no answer from the model
export interface MenuDescriptionsResponse {
  products: number;
  descriptions: MenuDescriptionsItem[];
  failed_item_ids: number[];
}

export interface MenuDataTagMergeExclusionTag {
  item_id: number;
  slug: string;
  name: string;
}

// A rejected merge suggestion, tag is the one with the lower id
export interface MenuDataTagMergeExclusion {
  tag: MenuDataTagMergeExclusionTag;
  other_tag: MenuDataTagMergeExclusionTag;
}

export interface MenuDataTagMergeExclusionsResponse {
  total: number;
  limit: number;
  offset: number;
  items: MenuDataTagMergeExclusion[];
}

// id is the tag in the path, tag_id the other tag of the pair
export interface MenuDataTagMergeExclusionBody {
  id: number;
  tag_id: number;
}

export interface MenuDataTagMergeSuggestionsResponse {
  total: number;
  limit: number;
  offset: number;
  items: MenuDataTagMergeSuggestion[];
}

// The kinds of names the review endpoints filter on ( item_type ), the same kinds as the tags
export type MenuDataTagReviewItemType = MenuDataTagKind;

export interface MenuDataTagReviewListParams {
  item_type?: MenuDataTagReviewItemType;
  // Default 50, at most 200
  limit?: number;
  offset?: number;
}

export interface MenuDataTagReviewStats {
  names: number;
  unclassified: number;
  suggestion_groups: number;
  suggestion_names: number;
  low_confidence: number;
  unmatched: number;
  approved_by_model: number;
  reviewed: number;
  menu_items_with_tag: number;
  menu_items_with_size: number;
}

// The tagging of the menu of one branch with the existing tags
export interface MenuDataTagBranchRun {
  state: "running" | "finished" | "failed" | string;
  started_at: string;
  finished_at?: string | null;
  // The names in the menu
  names?: number;
  // Of the names never classified before
  classified?: {
    names: number;
    exact: number;
    matched: number;
    left_for_full_run: number;
    failed_batches: number;
  };
  // Menu items whose tags changed
  applied?: {
    items: number;
    branches: number;
  };
}

export interface MenuDataTagBranchRunStatus {
  running: boolean;
  // null when the menu was never tagged this way
  run: MenuDataTagBranchRun | null;
}

// One menu item name ( alias ) the model classified
export interface MenuDataTagReviewName {
  alias_id: number;
  item_type: MenuDataTagReviewItemType;
  language: string;
  name: string;
  menu_item_count: number;
  confidence: number;
  size_tag_id: number | null;
  size_slug: string | null;
  variant_tag_ids: number[];
  variant_slugs: string[];
}

export interface MenuDataTagSuggestionGroup {
  suggested_slug: string;
  suggested_name: string;
  suggested_name_en: string;
  kinds: MenuDataTagKind[];
  menu_item_count: number;
  name_count: number;
  average_confidence: number;
  names: MenuDataTagReviewName[];
}

export interface MenuDataTagSuggestionAcceptBody {
  suggested_slug: string;
  // Slug, names, kind and parent default to the suggestion when left out
  slug?: string;
  name?: string;
  name_en?: string;
  kind?: MenuDataTagKind[];
  parent_tag_id?: number | null;
  // The names join this tag instead of creating a new one
  existing_tag_id?: number | null;
  // Left out and classified again by the next run
  exclude_alias_ids?: number[];
}

export interface MenuDataTagSuggestionAcceptResponse {
  menu_data_tag_id: number;
  alias_count: number;
  menu_items_changed: number;
  branches: number;
}

export interface MenuDataTagSuggestionRejectBody {
  suggested_slug: string;
}

export interface MenuDataTagSuggestionRejectResponse {
  alias_count: number;
  menu_items_changed: number;
  branches: number;
}

export interface MenuDataTagReviewMatchedName extends MenuDataTagReviewName {
  menu_data_tag_id: number | null;
  tag_slug: string | null;
}

export interface MenuDataTagLowConfidenceResponse {
  total: number;
  limit: number;
  offset: number;
  items: MenuDataTagReviewMatchedName[];
}

export type MenuDataTagUnmatchedResponse = MenuDataTagReviewMatchedName[];

// confirm: keep the model's tag and size, change: use menu_data_tag_id ( and size_tag_id when given, null removes
// the size ), none: no food tag, the size stays unless size_tag_id is given
export type MenuDataTagAliasDecideBody =
  | { action: "confirm" }
  | {
      action: "change";
      menu_data_tag_id: number;
      size_tag_id?: number | null;
      variant_tag_ids?: number[];
    }
  | { action: "none"; size_tag_id?: number | null };

export interface MenuDataTagAliasDecideResponse {
  menu_items_changed: number;
  branches: number;
}

export interface MenuDataTagRuleTagsParams {
  // Default 5
  min_branches?: number;
  limit?: number;
  offset?: number;
}

export interface MenuDataTagRuleTagName {
  alias_id: number;
  item_type: MenuDataTagReviewItemType;
  name: string;
  normalized_name: string;
  menu_item_count: number;
  size_tag_id: number | null;
}

export interface MenuDataTagRuleTag {
  slug: string;
  name: string;
  kinds: MenuDataTagKind[];
  branches: number;
  menu_item_count: number;
  names: MenuDataTagRuleTagName[];
}

export interface MenuDataTagRuleTagsResponse {
  total: number;
  names: number;
  menu_item_count: number;
  tags: MenuDataTagRuleTag[];
}

export interface MenuDataTagRuleTagsCreateBody {
  min_branches?: number;
  // How many tags to create, default 50, at most 200
  limit?: number;
  reclassify_leftovers?: boolean;
}

export interface MenuDataTagRuleTagsCreateResponse {
  tags_created: number;
  names_linked: number;
  names_reset: number;
  menu_items_changed: number;
  branches: number;
}

// Polls Appsmen sends to its customers
export interface PollOption {
  // Left out for an option that is not saved yet
  item_id?: number;
  text: string;
}

export interface PollBody {
  question: string;
  description?: string;
  can_choose_multiple_options: boolean;
  // "YYYY-MM-DD HH:mm"
  start_time: string;
  end_time: string;
  options: PollOption[];
}

export interface Poll extends PollBody {
  item_id: number;
  options: Required<PollOption>[];
  created_at: string;
}

export interface PollsResponse {
  total: number;
  limit: number;
  offset: number;
  items: Poll[];
}

export enum PollStatus {
  SCHEDULED = "scheduled",
  ACTIVE = "active",
  ENDED = "ended",
}

// A customer's view of a poll: the poll with its vote counts and own vote
export interface PollOptionResult {
  option_id: number;
  votes: number;
}

export interface PollResults {
  poll: Poll;
  results: PollOptionResult[];
  // Respondents, with multiple options allowed the option votes add up to more
  total_responses: number;
  // Empty when the customer has not voted yet
  my_option_ids: number[];
}

// One customer's answer to a poll, for the admin results
export interface PollResponse {
  item_id: number;
  poll_id: number;
  branch_id: number;
  branch_name: string;
  user_name: string;
  option_ids: number[];
  created_at: string;
}

export interface PollResponsesResponse {
  total: number;
  limit: number;
  offset: number;
  items: PollResponse[];
}

export interface PollResultsSummary {
  poll: Poll;
  total_responses: number;
  options: (PollOptionResult & { text: string })[];
  // One entry per day from the start until today or the end, "YYYY-MM-DD"
  daily: { date: string; responses: number }[];
}

// Messages Appsmen shows to its customers inside the CMS
export interface SystemMessageBody {
  title: string;
  // Html from the rich text editor, polls are <div data-type="poll" data-poll-id="1">
  content: string;
  // "YYYY-MM-DD HH:mm"
  start_date: string;
  end_date: string;
}

export interface SystemMessage extends SystemMessageBody {
  item_id: number;
  created_at: string;
  updated_at: string;
}

export interface SystemMessagesResponse {
  total: number;
  limit: number;
  offset: number;
  items: SystemMessage[];
}

export enum SystemMessageStatus {
  SCHEDULED = "scheduled",
  ACTIVE = "active",
  ENDED = "ended",
}
